import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Badge, Button, Checkbox, Spinner, Switch } from '@monksflow/monks-ui';
import { FileCheckIcon, FileUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatFileSize } from '@/shared/utils/format';
import {
  useAnalysisFormStore,
  UPLOADED_AUDIENCES_MAX,
  type ExtractedAudience,
} from '../../../model/analysisFormStore';
import {
  EXTRACTION_BYTES_MAX,
  EXTRACTION_FILES_MAX,
  useAudienceExtraction,
} from '../../../model/useAudienceExtraction';

/**
 * What the picker offers. Kept to what the extraction can actually read: the model reads a
 * document, so a spreadsheet or a slide deck is fair game, and an image is not. The legacy binary
 * `.ppt` and `.xls` are not: nothing extracts their text, and no model reads them as files.
 */
const ACCEPTED_FORMATS = ['.pdf', '.docx', '.pptx', '.xlsx', '.txt', '.md'].join(',');

interface AudienceOptionProps {
  audience: ExtractedAudience;
  selected: boolean;
  /** True once the report is holding as many audiences as it accepts. */
  disabled: boolean;
  keeping: boolean;
  onToggle: () => void;
  onToggleKeep: () => void;
}

/**
 * The card is the checkbox, as in the Knowledge hub tab beside it: one control per audience
 * rather than a button with a second control inside it.
 *
 * The keep switch sits inside it and stops the click from reaching the card, so asking for an
 * audience to be kept does not also unpick it.
 */
const AudienceOption = ({
  audience,
  selected,
  disabled,
  keeping,
  onToggle,
  onToggleKeep,
}: AudienceOptionProps) => {
  const { t } = useTranslation('analysis');

  const chips = [audience.ageRange, ...(audience.tags ?? [])].filter((chip): chip is string =>
    Boolean(chip),
  );

  // One line for where the text came from, as much of it as the extractor could honestly give:
  // the file on its own for a document that showed no page or heading, and nothing at all for
  // an audience several documents describe.
  const provenance = [audience.sourceFileName, audience.sourceLocation].filter(Boolean).join(' - ');

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    if (event.repeat) return;
    event.preventDefault();
    if (!disabled) onToggle();
  };

  return (
    <div
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled}
      aria-label={audience.name}
      tabIndex={0}
      onClick={() => {
        if (!disabled) onToggle();
      }}
      onKeyDown={handleKeyDown}
      className={[
        'flex gap-sm rounded-xl border px-md py-sm text-left transition-colors duration-fast ease-out',
        'focus-visible:focus-ring outline-none',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        selected
          ? 'border-primary bg-accent'
          : 'border-border-light bg-background hover:border-primary',
      ].join(' ')}
    >
      <Checkbox
        checked={selected}
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none mt-0.5 shrink-0"
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2xs">
        <span className="type-body-md-strong text-foreground">{audience.name}</span>

        {/* The age range leads, as the one trait every audience is read against; the traits the
            document gave follow in its own order. */}
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2xs">
            {chips.map((chip, index) => (
              <Badge key={`${chip}-${index}`} variant="secondary" size="sm" roundness="full">
                {chip}
              </Badge>
            ))}
          </div>
        )}

        {audience.description && (
          <p className="type-body-md text-foreground-subtle">{audience.description}</p>
        )}

        {provenance && <p className="type-body-sm text-foreground-subtle">{provenance}</p>}

        {/* Presentational on purpose: the switch inside is the control, and this only keeps a
            click on it from reaching the card and unpicking the audience. */}
        <div
          role="presentation"
          className="mt-2xs flex w-fit items-center gap-xs rounded-lg border border-border-light bg-background px-sm py-2xs"
          onClick={(event) => event.stopPropagation()}
        >
          {/* Only an audience the report is being built on can be kept: the payload carries the
              picked ones, so a keep set on any other is dropped at submit with the switch still
              showing it on. */}
          <Switch
            checked={keeping}
            disabled={!selected}
            onCheckedChange={onToggleKeep}
            aria-label={t('analysis.uploadDocumentTab.saveThisSegmentToThe')}
          />
          <span
            className={`type-body-sm ${selected ? 'text-foreground' : 'text-foreground-subtle'}`}
          >
            {t('analysis.uploadDocumentTab.saveThisSegmentToThe')}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Naming the audience by handing IQ a document instead of picking from the Knowledge hub.
 *
 * The run outlives the request that starts it, so this shows where the reading is rather than
 * blocking: a document is uploaded, read, and the audiences it described come back to be
 * picked. Each one can be asked to be kept in the Knowledge Hub, which happens once its
 * persona is built - after the report starts, not here.
 */
export const UploadDocumentTab = () => {
  const { t } = useTranslation('analysis');
  const inputRef = useRef<HTMLInputElement>(null);

  const extractedAudiences = useAnalysisFormStore((s) => s.extractedAudiences);
  const selectedExtractedIds = useAnalysisFormStore((s) => s.selectedExtractedIds);
  const extractedToSaveIds = useAnalysisFormStore((s) => s.extractedToSaveIds);
  const toggleExtractedAudience = useAnalysisFormStore((s) => s.toggleExtractedAudience);
  const toggleExtractedSaveToHub = useAnalysisFormStore((s) => s.toggleExtractedSaveToHub);

  const { status, fileNames, error, extract, retry } = useAudienceExtraction();
  const [tooManyPicked, setTooManyPicked] = useState(0);
  const [tooLargeBytes, setTooLargeBytes] = useState(0);

  // Read off the audiences rather than the run: the step unmounts when the wizard moves on, so
  // the run's own state is gone on the way back while the audiences it produced are not.
  //
  // An audience that several documents describe carries no file name, so this can come back
  // empty for a run whose audiences all span its documents.
  const readFileNames = [
    ...new Set(
      extractedAudiences
        .map((audience) => audience.sourceFileName)
        .filter((name): name is string => Boolean(name)),
    ),
  ];
  const atCap = selectedExtractedIds.length >= UPLOADED_AUDIENCES_MAX;

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Cleared so picking the same document twice still fires a change event.
    event.target.value = '';
    if (files.length === 0) return;

    // Refused before anything is uploaded: a run the API caps at five would otherwise send
    // every document to S3 first and only then be turned away, so the whole upload is spent
    // on an answer we already had. Said here rather than through the failure screen, which
    // would drop the audiences already on screen for a pick that never started.
    if (files.length > EXTRACTION_FILES_MAX) {
      setTooManyPicked(files.length);
      setTooLargeBytes(0);
      return;
    }

    // Same reason, the other ceiling: the API caps what one read hands the model, and storage
    // takes far more per file than the model does. Five documents inside the file count can
    // still be well over it, so two ordinary decks would spend the whole upload to be refused.
    const bytes = files.reduce((total, file) => total + file.size, 0);
    if (bytes > EXTRACTION_BYTES_MAX) {
      setTooManyPicked(0);
      setTooLargeBytes(bytes);
      return;
    }

    setTooManyPicked(0);
    setTooLargeBytes(0);
    void extract(files);
  };

  const browse = () => inputRef.current?.click();

  const extracted = extractedAudiences.length > 0;

  const summary = !extracted
    ? t('analysis.uploadDocumentTab.noAudiencesInThe')
    : readFileNames.length > 0
      ? t('analysis.uploadDocumentTab.nSegmentsExtracted', {
          count: extractedAudiences.length,
          files: readFileNames.join(', '),
        })
      : t('analysis.uploadDocumentTab.nSegmentsFound', { count: extractedAudiences.length });

  const tooManyNotice = (tooManyPicked > 0 || tooLargeBytes > 0) && (
    <p role="alert" className="type-body-sm text-destructive">
      {tooManyPicked > 0
        ? t('analysis.uploadDocumentTab.iqReadsUpToN', {
            max: EXTRACTION_FILES_MAX,
            picked: tooManyPicked,
          })
        : t('analysis.uploadDocumentTab.thoseDocumentsComeTo', {
            max: formatFileSize(EXTRACTION_BYTES_MAX),
            picked: formatFileSize(tooLargeBytes),
          })}
    </p>
  );

  const picker = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={ACCEPTED_FORMATS}
      onChange={handleFiles}
      className="hidden"
      aria-label={t('analysis.uploadDocumentTab.chooseDocuments')}
    />
  );

  // Only when there is genuinely nothing yet. Coming back to the step, the run is idle again
  // but the audiences - and the picks the report will be built on - are still in the store.
  if (status === 'idle' && extractedAudiences.length === 0) {
    return (
      <div className="flex flex-col items-center gap-xs rounded-xl border border-dashed border-border px-md py-3xl text-center mt-2">
        {picker}
        <span className="grid size-11 place-items-center rounded-[10px] bg-accent text-accent-foreground">
          <FileUpIcon className="size-5" />
        </span>
        <p className="type-body-md-strong text-foreground">
          {t('analysis.uploadDocumentTab.uploadAnAudienceDocument')}
        </p>
        <p className="type-body-md text-foreground-subtle">
          {t('analysis.uploadDocumentTab.iqReadsItAndExtracts')}
        </p>
        <Button variant="outline" onClick={browse} className="mt-2xs">
          {t('analysis.uploadDocumentTab.browseFiles')}
        </Button>
        {tooManyNotice}
      </div>
    );
  }

  if (status === 'uploading' || status === 'reading') {
    return (
      <div className="flex flex-col items-center gap-sm rounded-xl border border-dashed border-border px-md py-3xl">
        <Spinner />
        <p className="type-body-md text-foreground-subtle">
          {status === 'uploading'
            ? t('analysis.uploadDocumentTab.uploadingFiles', { files: fileNames.join(', ') })
            : t('analysis.uploadDocumentTab.extractingAudiencesFrom', {
                files: fileNames.join(', '),
              })}
        </p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-sm rounded-xl border border-dashed border-border px-md py-3xl text-center">
        {picker}
        <p className="type-body-md-strong text-foreground">
          {t('analysis.uploadDocumentTab.couldNotReadThe')}
        </p>
        {error && <p className="type-body-md text-foreground-subtle">{error}</p>}
        <div className="flex flex-wrap items-center gap-xs">
          <Button variant="outline" onClick={() => void retry()}>
            {t('analysis.uploadDocumentTab.tryAgain')}
          </Button>
          <Button variant="ghost" onClick={browse}>
            {t('analysis.uploadDocumentTab.uploadAnother')}
            <FileUpIcon />
          </Button>
        </div>
        {tooManyNotice}
        {extractedAudiences.length > 0 && (
          <p className="type-body-sm text-foreground-subtle">
            {t('analysis.uploadDocumentTab.whatEarlierDocumentsGave', {
              count: extractedAudiences.length,
            })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-md">
      {picker}

      {/* Green is the read that found something. A run that came back with no audiences ends
          up here too, and reads as the neutral answer it is rather than a success. */}
      <div
        data-testid="extraction-summary"
        className={[
          'flex flex-wrap items-center gap-xs rounded-lg px-md py-xs',
          extracted ? 'bg-positive text-positive-foreground' : 'bg-surface text-foreground-subtle',
        ].join(' ')}
      >
        <FileCheckIcon className="size-4" />
        <span className="type-body-md flex-1">{summary}</span>
        {/* Adds to the list rather than replacing it, so a report can be built from several
            documents. */}
        <Button variant="outline" size="sm" onClick={browse}>
          {t('analysis.uploadDocumentTab.uploadAnother')}
          <FileUpIcon />
        </Button>
      </div>

      {tooManyNotice}

      {atCap && (
        <p className="type-body-sm text-foreground-subtle">
          {t('analysis.uploadDocumentTab.atMostNAudiences', { count: UPLOADED_AUDIENCES_MAX })}
        </p>
      )}

      {extractedAudiences.length > 0 && (
        <div className="flex flex-col gap-sm">
          {extractedAudiences.map((audience) => (
            <AudienceOption
              key={audience.id}
              audience={audience}
              selected={selectedExtractedIds.includes(audience.id)}
              disabled={atCap && !selectedExtractedIds.includes(audience.id)}
              keeping={extractedToSaveIds.includes(audience.id)}
              onToggle={() => toggleExtractedAudience(audience.id)}
              onToggleKeep={() => toggleExtractedSaveToHub(audience.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
